import React, { useState, useEffect } from 'react';
import { Administrator } from '../types/administrators';
import { useUser } from '../context/UserContext';
import { authFetch } from '../utils/authFetch';
import { getStatusBadgeClasses } from '../utils/badgeHelpers';
import { Pencil, Trash2, Filter, X } from 'lucide-react';

function timeAgo(dateString: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const now = new Date();
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)} days ago`;
  return date.toLocaleDateString();
}

const roleBadge = (role: string) => {
  if (role.toLowerCase().includes('super')) {
    return <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-semibold">Super Admin</span>;
  }
  return <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold">Admin</span>;
};

const roleOptions = [
  { label: 'Admin', value: 'admin' },
  { label: 'Super Admin', value: 'super_admin' },
];
const statusOptions = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

const Administrators: React.FC = () => {
  const { accessToken, setUserAndToken, user } = useUser();
  const [admins, setAdmins] = useState<Administrator[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    role: '',
    status: 'active',
    password: '',
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editModal, setEditModal] = useState<{ open: boolean; admin: Administrator | null }>({ open: false, admin: null });
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    role: '',
    status: 'active',
    password: '',
  });
  const [editFormError, setEditFormError] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; admin: Administrator | null }>({ open: false, admin: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [infoModal, setInfoModal] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  const openEditModal = (admin: Administrator) => {
    setEditForm({
      name: admin.name,
      email: admin.email,
      role: admin.role,
      status: admin.status,
      password: '',
    });
    setEditFormError('');
    setEditModal({ open: true, admin });
  };

  const handleAddAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!form.name || !form.email || !form.role || !form.status || !form.password) {
      setFormError('All fields are required.');
      return;
    }
    setSaving(true);
    const resp = await authFetch(
      (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') + '/api/v1/admin/administrators',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      },
      accessToken,
      setUserAndToken
    );
    setSaving(false);
    if (resp.ok) {
      setShowModal(false);
      setForm({ name: '', email: '', role: '', status: 'active', password: '' });
      setFormError('');
      // Refresh list
      const data = await resp.json();
      setAdmins((prev) => [...prev, data]);
    } else {
      const err = await resp.json();
      setFormError(err.detail || 'Failed to add administrator.');
    }
  };

  const handleEditAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditFormError('');
    if (!editForm.name || !editForm.email || !editForm.role || !editForm.status) {
      setEditFormError('Name, email, role, and status are required.');
      return;
    }
    setEditSaving(true);
    const resp = await authFetch(
      (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') + `/api/v1/admin/administrators/${editModal.admin?.id}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editForm.name,
          email: editForm.email,
          role: editForm.role,
          status: editForm.status,
          password: editForm.password || undefined,
        }),
      },
      accessToken,
      setUserAndToken
    );
    setEditSaving(false);
    if (resp.ok) {
      const data = await resp.json();
      setAdmins((prev) => prev.map(a => a.id === data.id ? data : a));
      setEditModal({ open: false, admin: null });
      setEditForm({ name: '', email: '', role: '', status: 'active', password: '' });
      setEditFormError('');
    } else {
      const err = await resp.json();
      setEditFormError(err.detail || 'Failed to update administrator.');
    }
  };

  const openDeleteModal = (admin: Administrator) => {
    setDeleteModal({ open: true, admin });
    setDeleteError('');
  };

  const handleDeleteAdmin = async () => {
    if (!deleteModal.admin) return;
    setDeleteLoading(true);
    setDeleteError('');
    const resp = await authFetch(
      (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') + `/api/v1/admin/administrators/${deleteModal.admin.id}`,
      {
        method: 'DELETE',
      },
      accessToken,
      setUserAndToken
    );
    setDeleteLoading(false);
    if (resp.ok) {
      setAdmins((prev) => prev.filter(a => a.id !== deleteModal.admin?.id));
      setDeleteModal({ open: false, admin: null });
    } else {
      const err = await resp.json();
      setDeleteError(err.detail || 'Failed to delete administrator.');
    }
  };

  useEffect(() => {
    const fetchAdmins = async () => {
      setLoading(true);
      const resp = await authFetch(
        (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') + '/api/v1/admin/administrators',
        {},
        accessToken,
        setUserAndToken
      );
      if (resp.ok) {
        const data = await resp.json();
        setAdmins(data.data || []);
      }
      setLoading(false);
    };
    fetchAdmins();
    // eslint-disable-next-line
  }, []);

  const filteredAdmins = admins.filter(a =>
    a.name.toLowerCase().includes(search.toLowerCase()) ||
    a.email.toLowerCase().includes(search.toLowerCase())
  );

  const isSuperAdmin = user?.role?.toLowerCase().includes('super');

  return (
    <div className="w-full">
      {/* Add Administrator Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-8 relative animate-fadeIn">
            <button
              className="absolute top-6 right-6 text-slate-400 hover:text-slate-600"
              onClick={() => setShowModal(false)}
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-semibold text-slate-900 mb-6">Add Administrator</h2>
            <form onSubmit={handleAddAdmin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  className="w-full border border-slate-200 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="Enter full name"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email Address *</label>
                <input
                  type="email"
                  className="w-full border border-slate-200 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="Enter email address"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  required
                />
              </div>
              <div className="relative">
                <select
                  className="w-full border border-slate-200 rounded-lg px-4 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-100 appearance-none"
                  value={form.role}
                  onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                  required
                >
                  <option value="">Select a role</option>
                  {roleOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400">
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" strokeWidth="2" d="M6 9l6 6 6-6"/></svg>
                </span>
              </div>
              <div className="relative">
                <select
                  className="w-full border border-slate-200 rounded-lg px-4 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-100 appearance-none"
                  value={form.status}
                  onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                  required
                >
                  {statusOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400">
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" strokeWidth="2" d="M6 9l6 6 6-6"/></svg>
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Password *</label>
                <input
                  type="password"
                  className="w-full border border-slate-200 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="Enter password"
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  required
                />
              </div>
              {formError && <div className="text-red-500 text-sm">{formError}</div>}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  className="px-5 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition"
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 rounded-lg bg-[#4a67b7] text-white font-semibold shadow hover:bg-primary/90 transition"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Administrator'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Edit Administrator Modal */}
      {editModal.open && editModal.admin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-8 relative animate-fadeIn">
            <button
              className="absolute top-6 right-6 text-slate-400 hover:text-slate-600"
              onClick={() => setEditModal({ open: false, admin: null })}
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-semibold text-slate-900 mb-6">Edit Administrator</h2>
            <form onSubmit={handleEditAdmin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  className="w-full border border-slate-200 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="Enter full name"
                  value={editForm.name}
                  onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email Address *</label>
                <input
                  type="email"
                  className="w-full border border-slate-200 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="Enter email address"
                  value={editForm.email}
                  onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                  required
                />
              </div>
              <div className="relative">
                <select
                  className="w-full border border-slate-200 rounded-lg px-4 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-100 appearance-none"
                  value={editForm.role}
                  onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))}
                  required
                >
                  <option value="">Select a role</option>
                  {roleOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400">
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" strokeWidth="2" d="M6 9l6 6 6-6"/></svg>
                </span>
              </div>
              <div className="relative">
                <select
                  className="w-full border border-slate-200 rounded-lg px-4 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-100 appearance-none"
                  value={editForm.status}
                  onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
                  required
                >
                  {statusOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400">
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" strokeWidth="2" d="M6 9l6 6 6-6"/></svg>
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                <input
                  type="password"
                  className="w-full border border-slate-200 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="Leave blank to keep current password"
                  value={editForm.password}
                  onChange={e => setEditForm(f => ({ ...f, password: e.target.value }))}
                />
              </div>
              {editFormError && <div className="text-red-500 text-sm">{editFormError}</div>}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  className="px-5 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition"
                  onClick={() => setEditModal({ open: false, admin: null })}
                  disabled={editSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 rounded-lg bg-[#4a67b7] text-white font-semibold shadow hover:bg-primary/90 transition"
                  disabled={editSaving}
                >
                  {editSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Delete Administrator Modal */}
      {deleteModal.open && deleteModal.admin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 relative animate-fadeIn">
            <button
              className="absolute top-6 right-6 text-slate-400 hover:text-slate-600"
              onClick={() => setDeleteModal({ open: false, admin: null })}
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Delete Administrator</h2>
            <p className="mb-6 text-slate-700">Are you sure you want to delete <span className="font-semibold">{deleteModal.admin.name}</span>? This action cannot be undone.</p>
            {deleteError && <div className="text-red-500 text-sm mb-4">{deleteError}</div>}
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                className="px-5 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition"
                onClick={() => setDeleteModal({ open: false, admin: null })}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-6 py-2 rounded-lg bg-red-600 text-white font-semibold shadow hover:bg-red-700 transition"
                onClick={handleDeleteAdmin}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Info Modal for Disabled Actions */}
      {infoModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 relative animate-fadeIn">
            <button
              className="absolute top-6 right-6 text-slate-400 hover:text-slate-600"
              onClick={() => setInfoModal({ open: false, message: '' })}
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Action Not Allowed</h2>
            <p className="mb-6 text-slate-700">{infoModal.message}</p>
            <div className="flex justify-end mt-6">
              <button
                type="button"
                className="px-6 py-2 rounded-lg bg-[#4a67b7] text-white font-semibold shadow hover:bg-primary/90 transition"
                onClick={() => setInfoModal({ open: false, message: '' })}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg text-slate-900 mb-1 px-2">Manage system administrators and their permissions</h2>
        </div>
        <button className="bg-[#4a67b7] hover:bg-primary/90 text-white px-6 py-2 rounded-lg flex items-center shadow transition" onClick={() => setShowModal(true)}>
          + Add Administrator
        </button>
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-lg text-slate-900">All Administrators</h3>
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search administrators..."
                className="pl-4 pr-10 py-2 rounded-lg border border-slate-200 bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-100 text-sm"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <span className="absolute right-3 top-2.5 text-slate-400">
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" strokeWidth="2" d="M21 21l-4.35-4.35m2.1-5.4a7.5 7.5 0 11-15 0 7.5 7.5 0 0115 0z"/></svg>
              </span>
            </div>
            <button className="ml-2 p-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-500" title="Filter">
              <Filter className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-slate-500 border-b border-slate-100">
                <th className="px-6 py-3 text-left font-semibold">ADMINISTRATOR</th>
                <th className="px-6 py-3 text-left font-semibold">ROLE</th>
                <th className="px-6 py-3 text-left font-semibold">STATUS</th>
                <th className="px-6 py-3 text-left font-semibold">LAST LOGIN</th>
                <th className="px-6 py-3 text-left font-semibold">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center py-8 text-slate-400">Loading...</td></tr>
              ) : filteredAdmins.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-8 text-slate-400">No administrators found.</td></tr>
              ) : filteredAdmins.map((admin) => (
                <tr key={admin.id} className="border-b border-slate-50 hover:bg-slate-50 transition">
                  <td className="px-6 py-4 text-left flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-bold text-base">
                      {admin.name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">{admin.name}</div>
                      <div className="text-slate-500 text-xs">{admin.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-left">{roleBadge(admin.role)}</td>
                  <td className="px-6 py-4 text-left">
                    <span className={getStatusBadgeClasses(admin.status)}>
                      {admin.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-left">{timeAgo(admin.last_login)}</td>
                  <td className="px-6 py-4 text-left flex gap-3">
                    <button
                      className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
                      title={
                        !isSuperAdmin && admin.id !== user?.id ? 'Only super-admin can edit other admins' : 'Edit'
                      }
                      onClick={() => {
                        if (!isSuperAdmin && admin.id !== user?.id) {
                          setInfoModal({ open: true, message: 'Only super-admin can edit other administrators.' });
                        } else {
                          openEditModal(admin);
                        }
                      }}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      className="p-2 rounded-lg hover:bg-slate-100 text-red-500"
                      title={
                        admin.id === user?.id
                          ? 'You cannot delete your own account'
                          : !isSuperAdmin
                          ? 'Only super-admin can delete other admins'
                          : 'Delete'
                      }
                      onClick={() => {
                        if (admin.id === user?.id) {
                          setInfoModal({ open: true, message: 'You cannot delete your own account.' });
                        } else if (!isSuperAdmin && admin.id !== user?.id) {
                          setInfoModal({ open: true, message: 'Only super-admin can delete other administrators.' });
                        } else {
                          openDeleteModal(admin);
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-3 text-slate-500 text-sm border-t border-slate-100">
          Showing 1 to {filteredAdmins.length} of {admins.length} results
        </div>
      </div>
    </div>
  );
};

export default Administrators; 